import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Linkedin, Film, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import api, { formatApiError } from "../../lib/api";

const CHUNK = 5 * 1024 * 1024;
const IMG_MAX = 10 * 1024 * 1024;
const VID_MAX = 100 * 1024 * 1024;
const isVid = (f) => f.type.startsWith("video/") || /\.(mp4|webm|mov)$/i.test(f.name);
const hex32 = () => Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) => b.toString(16).padStart(2, "0")).join("");

async function uploadOne(file, onPct) {
  if (!isVid(file)) {
    if (file.size > IMG_MAX) throw new Error("Photo is over 10 MB");
    const fd = new FormData();
    fd.append("file", file);
    const r = await api.post("/website/team-photo?folder=gallery", fd, { onUploadProgress: (e) => onPct(Math.round((e.loaded / (e.total || 1)) * 100)) });
    return { path: r.data.path, media_type: "image" };
  }
  if (file.size > VID_MAX) throw new Error("Video is over 100 MB");
  const id = hex32();
  const total = Math.max(1, Math.ceil(file.size / CHUNK));
  let res;
  for (let i = 0; i < total; i += 1) {
    const fd = new FormData();
    fd.append("upload_id", id); fd.append("index", i); fd.append("total", total); fd.append("filename", file.name);
    fd.append("file", file.slice(i * CHUNK, (i + 1) * CHUNK), file.name);
    res = await api.post("/website/video-chunk", fd);
    onPct(Math.round(((i + 1) / total) * 100));
  }
  return { path: res.data.path, media_type: "video" };
}

export function BulkUpload({ onAdded }) {
  const input = useRef(null);
  const [files, setFiles] = useState([]);
  const [cat, setCat] = useState("Office");
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const setOne = (i, patch) => setFiles((fs) => fs.map((f, n) => (n === i ? { ...f, ...patch } : f)));

  const start = async () => {
    setBusy(true);
    const added = [];
    for (let i = 0; i < files.length; i += 1) {
      if (files[i].state !== "ready") continue;
      setOne(i, { state: "uploading", pct: 0 });
      try {
        const up = await uploadOne(files[i].file, (pct) => setOne(i, { pct }));
        const r = await api.post("/website/content/gallery", { photo: up.path, media_type: up.media_type, category: cat, caption, published: true });
        added.push(r.data);
        setOne(i, { state: "done", pct: 100 });
      } catch (err) { setOne(i, { state: "error", error: err.response ? formatApiError(err) : err.message }); }
    }
    setBusy(false);
    if (added.length) { onAdded(added.reverse()); toast.success(`${added.length} item(s) added to Life at VCAJ`); }
  };

  const pick = (e) => { setFiles(Array.from(e.target.files || []).map((file) => ({ file, state: "ready", pct: 0 }))); e.target.value = ""; };
  const done = !files.some((f) => f.state === "ready");

  return (
    <div className="bg-white border border-dashed border-slate-300 rounded-sm p-5 space-y-4" data-testid="bulk-upload">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="outline" onClick={() => input.current?.click()} disabled={busy} data-testid="bulk-pick-btn"><Upload className="h-4 w-4 mr-1" /> Choose photos & videos</Button>
        <input ref={input} type="file" multiple accept="image/png,image/jpeg,image/webp,video/mp4,video/webm,video/quicktime" className="hidden" onChange={pick} data-testid="bulk-file-input" />
        <span className="text-xs text-slate-500">Select many at once. Photos up to 10 MB, videos (MP4/WEBM/MOV) up to 100 MB.</span>
      </div>
      {files.length > 0 && (
        <>
          <div className="grid md:grid-cols-3 gap-3">
            <Select value={cat} onValueChange={setCat}>
              <SelectTrigger data-testid="bulk-category"><SelectValue /></SelectTrigger>
              <SelectContent>{["Office", "Celebrations", "Events"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <Input className="md:col-span-2" placeholder="Caption for all (optional, e.g. Diwali celebration 2025)" value={caption} onChange={(e) => setCaption(e.target.value)} data-testid="bulk-caption" />
          </div>
          <ul className="divide-y divide-slate-100 text-sm max-h-60 overflow-y-auto" data-testid="bulk-file-list">
            {files.map((f, i) => (
              <li key={`${f.file.name}-${i}`} className="py-2 flex items-center gap-3" data-testid={`bulk-file-${i}`}>
                {isVid(f.file) ? <Film className="h-4 w-4 text-slate-400" /> : <img src={URL.createObjectURL(f.file)} alt="" className="h-8 w-10 object-cover rounded-sm" />}
                <span className="flex-1 truncate">{f.file.name} <span className="text-slate-400">· {(f.file.size / 1048576).toFixed(1)} MB</span></span>
                {f.state === "uploading" && <span className="flex items-center gap-2 text-xs text-slate-500"><Loader2 className="h-3.5 w-3.5 animate-spin" />{f.pct}%</span>}
                {f.state === "done" && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
                {f.state === "error" && <span className="flex items-center gap-1 text-xs text-rose-600" title={f.error}><AlertCircle className="h-4 w-4" />{f.error}</span>}
              </li>
            ))}
          </ul>
          <div className="flex gap-2">
            <Button onClick={start} disabled={busy || done} data-testid="bulk-upload-btn">{busy ? "Uploading…" : `Upload ${files.filter((f) => f.state === "ready").length} item(s)`}</Button>
            <Button variant="ghost" onClick={() => setFiles([])} disabled={busy} data-testid="bulk-clear-btn">Clear</Button>
          </div>
        </>
      )}
    </div>
  );
}

const CATS = ["Direct Tax", "GST & Indirect Tax", "Audit Standards", "Corporate Law", "ESG & Risk"];

export function LinkedInPostDialog({ onClose, onSaved }) {
  const [f, setF] = useState({ external_url: "", title: "", summary: "", category: CATS[0], author: "VCAJ & Associates LLP", published_on: new Date().toISOString().slice(0, 10) });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    if (!/^https:\/\/(www\.)?linkedin\.com\//.test(f.external_url)) { toast.error("Paste the full LinkedIn post link (https://www.linkedin.com/...)"); setBusy(false); return; }
    try { onSaved((await api.post("/website/content/publications", { ...f, source: "linkedin", read_minutes: 1, published: true })).data); toast.success("LinkedIn post added to Insights"); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl" data-testid="linkedin-post-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Linkedin className="h-5 w-5 text-[#0A66C2]" /> Add LinkedIn post to Insights</DialogTitle>
          <DialogDescription>On LinkedIn, open the post, click ··· → "Copy link to post" and paste it here.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input placeholder="https://www.linkedin.com/posts/..." value={f.external_url} onChange={(e) => setF({ ...f, external_url: e.target.value.trim() })} data-testid="li-url" />
          <Input placeholder="Headline shown on the website" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="li-title" />
          <Textarea rows={3} placeholder="One or two lines about the post (optional)" value={f.summary} onChange={(e) => setF({ ...f, summary: e.target.value })} data-testid="li-summary" />
          <div className="grid grid-cols-2 gap-3">
            <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
              <SelectTrigger data-testid="li-category"><SelectValue /></SelectTrigger>
              <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <Input type="date" value={f.published_on} onChange={(e) => setF({ ...f, published_on: e.target.value })} data-testid="li-date" />
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={busy || !f.external_url || !f.title} data-testid="li-save-btn">Add to Insights</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Stat = ({ label, value, id }) => (
  <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid={`visitors-stat-${id}`}>
    <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</div>
    <div className="text-3xl font-serif mt-2">{value}</div>
  </div>
);

const TopList = ({ title, rows, id }) => {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid={`visitors-list-${id}`}>
      <div className="text-xs uppercase tracking-wider text-slate-500 mb-3">{title}</div>
      {rows.length === 0 && <div className="text-sm text-slate-400">No data yet</div>}
      <ul className="space-y-2">
        {rows.map((r) => (
          <li key={r.name} className="relative text-sm flex justify-between px-2 py-1">
            <span className="absolute inset-y-0 left-0 bg-slate-100 rounded-sm" style={{ width: `${(r.count / max) * 100}%` }} />
            <span className="relative truncate pr-3">{r.name}</span><span className="relative tabular-nums text-slate-600">{r.count}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export function VisitorsTab() {
  const [days, setDays] = useState("30");
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); api.get("/website/visitors", { params: { days } }).then((r) => setD(r.data)).catch((e) => setD({ error: formatApiError(e) })); }, [days]);
  return (
    <div className="space-y-5" data-testid="visitors-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-500">Website traffic with visitor IP addresses. No names or emails are collected.</p>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-40" data-testid="visitors-range"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="7">Last 7 days</SelectItem><SelectItem value="30">Last 30 days</SelectItem><SelectItem value="90">Last 90 days</SelectItem><SelectItem value="365">Last 12 months</SelectItem></SelectContent>
        </Select>
      </div>
      {d?.error && <div className="text-sm text-rose-600" data-testid="visitors-error">{d.error}</div>}
      {!d ? <div className="text-sm text-slate-500">Loading…</div> : !d.error && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <Stat id="unique" label="Unique visitors" value={d.unique_visitors} />
            <Stat id="new" label="New visitors" value={d.new_visitors} />
            <Stat id="visits" label="Visits" value={d.visits} />
            <Stat id="views" label="Page views" value={d.page_views} />
            <Stat id="ppv" label="Pages per visit" value={d.pages_per_visit} />
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-5 h-72" data-testid="visitors-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={d.series} margin={{ left: -20, right: 10, top: 10 }}>
                <CartesianGrid stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="date" tickFormatter={(v) => v.slice(5)} tick={{ fontSize: 11 }} minTickGap={20} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Area type="monotone" dataKey="views" name="Page views" stroke="#14263D" fill="#14263D" fillOpacity={0.08} />
                <Area type="monotone" dataKey="visitors" name="Visitors" stroke="#C4A66A" fill="#C4A66A" fillOpacity={0.15} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <TopList id="pages" title="Top pages" rows={d.top_pages} />
            <TopList id="sources" title="Where visitors came from" rows={d.sources} />
            <TopList id="countries" title="Countries" rows={d.countries} />
            <TopList id="devices" title="Devices" rows={d.devices} />
            <TopList id="browsers" title="Browsers" rows={d.browsers} />
            <TopList id="ips" title="Top IP addresses" rows={d.top_ips || []} />
          </div>
          <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto" data-testid="visitors-recent">
            <div className="text-xs uppercase tracking-wider text-slate-500 p-4 pb-2">Recent visits (latest 100)</div>
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase tracking-wider text-slate-500 bg-slate-50"><tr>{["Time (IST)", "IP address", "Page", "Source", "Country", "Device", "Browser"].map((h) => <th key={h} className="text-left px-3 py-2 font-medium">{h}</th>)}</tr></thead>
              <tbody className="divide-y divide-slate-100">
                {(d.recent || []).length === 0 && <tr><td colSpan={7} className="p-6 text-center text-slate-400">No visits recorded yet</td></tr>}
                {(d.recent || []).map((r, i) => (
                  <tr key={i} data-testid={`visit-row-${i}`}>
                    <td className="px-3 py-2 whitespace-nowrap">{new Date(r.ts.endsWith("Z") || r.ts.includes("+") ? r.ts : `${r.ts}Z`).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}</td>
                    <td className="px-3 py-2 font-mono text-xs" data-testid={`visit-ip-${i}`}>{r.ip || "—"}</td><td className="px-3 py-2">{r.path}</td><td className="px-3 py-2">{r.source}</td>
                    <td className="px-3 py-2">{r.country}</td><td className="px-3 py-2">{r.device}</td><td className="px-3 py-2">{r.browser}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
